import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Select } from '@ngxs/store';
import { filter, fromEvent, merge, Observable, Subject, takeUntil, timer } from 'rxjs';
import { CoachRoles } from 'app/modules/coach/enums/coach-roles.enum';
import { UserData } from 'app/modules/user-profile/interfaces/user-profile.interface';
import { UserProfileSelectors } from 'app/modules/user-profile/state/user-profile.selector';
import {
  EMAIL_REPORTS_BANNER_WINDOWS,
  getEmailReportsBannerWindow,
} from '../../utils/email-reports-banner.util';
import { NoticeBannerComponent } from '../notice-banner/notice-banner.component';

@Component({
  selector: 'og-email-reports-banner',
  standalone: true,
  imports: [CommonModule, NoticeBannerComponent],
  template: `
    <og-notice-banner
      *ngIf="showBanner()"
      theme="information"
      dismissLabel="Dismiss email reports notice"
      (dismiss)="closeBanner()"
    >
      <strong>For the best player experience, please send reports to your players email.</strong>
      Players can open the report, tap any link, and download the Optimum360 mobile app to watch
      video alongside their play-by-play breakdown. Reports sent by text are images only and are not
      interactive.
    </og-notice-banner>
  `,
  styles: [':host { display: contents; }', 'strong { display: block; margin-bottom: 0.35rem; }'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailReportsBannerComponent implements OnInit, OnDestroy {
  @Select(UserProfileSelectors.getUserProfileState) userData$: Observable<UserData | null>;
  showBanner = signal(false);
  private user: UserData | null = null;
  private dismissalKey: string | null = null;
  private dismissed = new Set<string>();
  private destroy$ = new Subject<void>();

  constructor(private router: Router) {}

  ngOnInit(): void {
    this.userData$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      this.user = user;
      this.updateVisibility();
    });
    // Update at each boundary even if the homepage stays open, and on return to a sleeping tab.
    const now = Date.now();
    const boundaries = EMAIL_REPORTS_BANNER_WINDOWS.flatMap(({ start, end }) => [start, end])
      .map((value) => new Date(value).getTime())
      .filter((value) => value > now);
    merge(
      this.router.events.pipe(filter((event) => event instanceof NavigationEnd)),
      fromEvent(window, 'focus'),
      fromEvent(document, 'visibilitychange'),
      ...boundaries.map((time) => timer(time - now))
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateVisibility());
  }

  private updateVisibility(): void {
    const window = getEmailReportsBannerWindow();
    const coach = this.user?.coach;
    const roles = [...(coach?.roles ?? []), coach?.primary_role, ...(this.user?.group_list ?? [])];
    const isCoach =
      !!coach && roles.some((role) => Object.values(CoachRoles).includes(role as CoachRoles));
    const isHomepage = this.router.url.split(/[?#]/)[0].replace(/\/$/, '') === '/dashboard';
    // A separate per-coach, per-week key allows the second week's notice to return.
    this.dismissalKey =
      window && this.user ? `emailReportsBannerDismissed:${this.user.pk}:${window.start}` : null;
    let dismissed = this.dismissalKey && this.dismissed.has(this.dismissalKey);
    try {
      dismissed =
        dismissed || (this.dismissalKey && localStorage.getItem(this.dismissalKey) === 'true');
    } catch {
      /* Keep dismissal usable when browser storage is unavailable. */
    }

    this.showBanner.set(!!window && isCoach && isHomepage && !dismissed);
  }

  closeBanner(): void {
    if (!this.dismissalKey) return;
    this.dismissed.add(this.dismissalKey);
    try {
      localStorage.setItem(this.dismissalKey, 'true');
    } catch {
      /* In-memory dismissal still works. */
    }
    this.showBanner.set(false);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
